export type SaveState =
  | "editing"
  | "saving"
  | "saved"
  | "remote-update"
  | "offline-retry"
  | "needs-resolution";

export const SAVE_STATE_LABELS: Record<SaveState, string> = {
  editing: "Editing",
  saving: "Saving…",
  saved: "Saved",
  "remote-update": "Remote update",
  "offline-retry": "Offline retry",
  "needs-resolution": "Needs resolution",
};

type Flushable = () => Promise<void>;
type HistoryControls = { undo?: () => Promise<void>; redo?: () => Promise<void> };
type Registration = { state: SaveState; canUndo: boolean; canRedo: boolean; touchedAt: number; controls: HistoryControls };

class SaveCoordinator {
  private readonly pending = new Map<Flushable, Registration>();
  private readonly listeners = new Set<() => void>();

  register(flush: Flushable, controls: HistoryControls = {}) {
    this.pending.set(flush, { state: "saved", canUndo: false, canRedo: false, touchedAt: 0, controls });
    this.emit();
    return {
      setState: (state: SaveState) => { const current = this.pending.get(flush); if (!current) return; this.pending.set(flush, { ...current, state, touchedAt: state === "editing" || state === "saving" ? Date.now() : current.touchedAt }); this.emit(); },
      setHistory: (canUndo: boolean, canRedo: boolean) => { const current = this.pending.get(flush); if (!current) return; this.pending.set(flush, { ...current, canUndo, canRedo }); this.emit(); },
      unregister: () => { this.pending.delete(flush); this.emit(); },
    };
  }

  async flushAll() {
    await Promise.all([...this.pending.keys()].map((flush) => flush()));
  }

  subscribe = (listener: () => void) => { this.listeners.add(listener); return () => this.listeners.delete(listener); };

  getSnapshot = (): SaveState => {
    const states = [...this.pending.values()].map((item) => item.state);
    if (states.includes("needs-resolution")) return "needs-resolution";
    if (states.includes("offline-retry")) return "offline-retry";
    if (states.includes("saving")) return "saving";
    if (states.includes("editing")) return "editing";
    if (states.includes("remote-update")) return "remote-update";
    return "saved";
  };

  getCommandSnapshot = () => {
    const rows = [...this.pending.values()];
    return `${rows.some((item) => item.canUndo) ? "undo" : "-"}:${rows.some((item) => item.canRedo) ? "redo" : "-"}`;
  };

  async undoLast() {
    const current = [...this.pending.values()].filter((item) => item.canUndo && item.controls.undo).sort((left, right) => right.touchedAt - left.touchedAt)[0];
    await current?.controls.undo?.();
  }

  async redoLast() {
    const current = [...this.pending.values()].filter((item) => item.canRedo && item.controls.redo).sort((left, right) => right.touchedAt - left.touchedAt)[0];
    await current?.controls.redo?.();
  }

  private emit() { this.listeners.forEach((listener) => listener()); }
}

export const authoringSaveCoordinator = new SaveCoordinator();
